/**
 * Gantt Chart Component
 *
 * Displays roadmap themes and items in a Gantt chart format.
 * Supports year (4 quarters), quarter (4 rolling quarters), and month (36 months) views.
 */

import { useMemo, useRef, useEffect } from 'react';

import { GanttRow } from './GanttRow';

import type { RoadmapThemeWithItems, RoadmapItemWithInvestment } from '@medsuite/models';

// Zoom levels: year (full year Q1-Q4), quarter (4 rolling quarters), month (36 months scrollable)
type ZoomLevel = 'year' | 'quarter' | 'month';

// Month names for display
const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

interface GanttChartProps {
  themes: RoadmapThemeWithItems[];
  year: number;
  startQuarter?: number; // 1-4, which quarter to start from (default: 1)
  zoomLevel?: ZoomLevel; // 'year', 'quarter', or 'month' (default: 'quarter')
  startMonth?: number; // 0-11, used for initial scroll position in month view
  onItemClick: (item: RoadmapItemWithInvestment) => void;
}

// Helper to get quarter start month (0-indexed)
const getQuarterStartMonth = (quarter: number) => (quarter - 1) * 3;

// Helper to get quarter end month (0-indexed)
const getQuarterEndMonth = (quarter: number) => quarter * 3 - 1;

interface TimeColumn {
  label: string;
  year: number;
  start: Date;
  end: Date;
  isCurrentMonth?: boolean;
}

// Width of each month column in pixels (for month view scrolling)
// Set to ~140px so 12 months (~1680px) + theme (192px) = ~1872px fits on most screens
const MONTH_COLUMN_WIDTH = 140;
// Width of the theme column
const THEME_COLUMN_WIDTH = 192; // w-48 = 12rem = 192px

/**
 * GanttChart Component
 *
 * Features:
 * - Year view: Q1-Q4 of a single year
 * - Quarter view: 4 rolling quarters starting from startQuarter
 * - Month view: 36 months (prev year + current year + next year) with horizontal scroll
 * - Theme sections as row groups
 * - Items rendered as horizontal bars
 */
export function GanttChart({
  themes,
  year,
  startQuarter = 1,
  zoomLevel = 'quarter',
  startMonth = 0,
  onItemClick,
}: GanttChartProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  // Sort themes by sequence
  const sortedThemes = useMemo(() => {
    return [...themes].sort((a, b) => a.theme_sequence - b.theme_sequence);
  }, [themes]);

  // Calculate time columns based on zoom level
  const columns = useMemo<TimeColumn[]>(() => {
    if (zoomLevel === 'year') {
      // Full year view: Q1-Q4 of the selected year
      const result: TimeColumn[] = [];
      for (let q = 1; q <= 4; q++) {
        const qStartMonth = getQuarterStartMonth(q);
        const qEndMonth = getQuarterEndMonth(q);
        const endDate = new Date(year, qEndMonth + 1, 0);

        result.push({
          label: `Q${q}`,
          year: year,
          start: new Date(year, qStartMonth, 1),
          end: endDate,
        });
      }
      return result;
    } else if (zoomLevel === 'quarter') {
      // Rolling 4 quarters starting from startQuarter
      const result: TimeColumn[] = [];
      for (let i = 0; i < 4; i++) {
        const quarterNum = ((startQuarter - 1 + i) % 4) + 1;
        const quarterYear = startQuarter + i > 4 ? year + 1 : year;
        const qStartMonth = getQuarterStartMonth(quarterNum);
        const qEndMonth = getQuarterEndMonth(quarterNum);

        // Get the last day of the end month
        const endDate = new Date(quarterYear, qEndMonth + 1, 0);

        result.push({
          label: `Q${quarterNum}`,
          year: quarterYear,
          start: new Date(quarterYear, qStartMonth, 1),
          end: endDate,
        });
      }
      return result;
    } else {
      // Month view: 36 months (previous year, current year, next year)
      const result: TimeColumn[] = [];
      const baseYear = year; // The "current" year for the view

      for (let yearOffset = -1; yearOffset <= 1; yearOffset++) {
        const colYear = baseYear + yearOffset;
        for (let month = 0; month < 12; month++) {
          const endDate = new Date(colYear, month + 1, 0);
          const isCurrentMonth = colYear === currentYear && month === currentMonth;

          result.push({
            label: MONTH_NAMES[month],
            year: colYear,
            start: new Date(colYear, month, 1),
            end: endDate,
            isCurrentMonth,
          });
        }
      }
      return result;
    }
  }, [year, startQuarter, zoomLevel, currentMonth, currentYear]);

  // Calculate the index of the current month in the columns array (for scrolling)
  const currentMonthIndex = useMemo(() => {
    if (zoomLevel !== 'month') {
      return 0;
    }
    // In month view, find the column that matches the startMonth and year
    return columns.findIndex(
      (col) => col.year === year && new Date(col.start).getMonth() === startMonth
    );
  }, [columns, zoomLevel, year, startMonth]);

  // Scroll to the current month when entering month view
  useEffect(() => {
    if (zoomLevel === 'month' && scrollContainerRef.current && currentMonthIndex >= 0) {
      // Calculate scroll position: column index * column width
      const scrollPosition = currentMonthIndex * MONTH_COLUMN_WIDTH;
      scrollContainerRef.current.scrollLeft = scrollPosition;
    }
  }, [zoomLevel, currentMonthIndex]);

  // Calculate the overall range start and end
  const rangeStart = columns[0].start;
  const rangeEnd = columns[columns.length - 1].end;

  // Number of columns for grid lines
  const columnCount = columns.length;

  // Determine if we should show the year in the column header
  const showYearInHeader = zoomLevel !== 'year';

  // Calculate minimum width based on zoom level
  const getMinWidth = () => {
    if (zoomLevel === 'month') {
      // 36 months * column width + theme column
      return `${36 * MONTH_COLUMN_WIDTH + THEME_COLUMN_WIDTH}px`;
    }
    return '900px';
  };

  return (
    <div ref={scrollContainerRef} className="overflow-x-auto">
      <div style={{ minWidth: getMinWidth() }}>
        {/* Header Row */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 sticky top-0 z-10">
          {/* Theme Column Header */}
          <div
            className="px-4 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 sticky left-0 z-20"
            style={{ width: THEME_COLUMN_WIDTH, minWidth: THEME_COLUMN_WIDTH }}
          >
            Theme
          </div>
          {/* Time Column Headers */}
          <div className="flex-1 flex">
            {columns.map((col, idx) => (
              <div
                key={`${col.label}-${col.year}-${idx}`}
                className={`px-2 py-3 text-center text-sm font-semibold border-r border-gray-200 dark:border-gray-700 last:border-r-0 ${
                  col.isCurrentMonth
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    : 'text-gray-700 dark:text-gray-300'
                }`}
                style={
                  zoomLevel === 'month'
                    ? { width: MONTH_COLUMN_WIDTH, minWidth: MONTH_COLUMN_WIDTH }
                    : { flex: 1 }
                }
              >
                {col.label}
                {showYearInHeader && (
                  <span
                    className={`ml-1 ${zoomLevel === 'month' ? 'text-xs text-gray-500 dark:text-gray-400 block' : ''}`}
                  >
                    {zoomLevel === 'month' ? col.year : ` ${col.year}`}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Theme Rows */}
        {sortedThemes.map((theme) => (
          <GanttRow
            key={theme.roadmap_theme_id}
            theme={theme}
            items={theme.items || []}
            yearStart={rangeStart}
            yearEnd={rangeEnd}
            columnCount={columnCount}
            zoomLevel={zoomLevel}
            onItemClick={onItemClick}
          />
        ))}

        {/* Empty State */}
        {sortedThemes.length === 0 && (
          <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
            No themes to display
          </div>
        )}
      </div>
    </div>
  );
}

export default GanttChart;
